// AES-GCM encryption for credentials stored in D1 (#30). The key is a Worker secret
// (SETTINGS_ENCRYPTION_KEY, base64, 32 bytes) and never lives in the database, so a copy of D1
// alone reveals nothing. Format: `v1.<iv>.<ciphertext>`, both base64url.

const VERSION = 'v1'
const enc = new TextEncoder()
const dec = new TextDecoder()

const toB64 = (bytes: Uint8Array) => btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
const fromB64 = (s: string): Uint8Array<ArrayBuffer> => Uint8Array.from(atob(s.replace(/-/g, '+').replace(/_/g, '/')), (c) => c.charCodeAt(0))

export class SecretBoxError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'SecretBoxError'
  }
}

async function importKey(keyB64: string | undefined) {
  if (!keyB64) throw new SecretBoxError('SETTINGS_ENCRYPTION_KEY is not configured')
  let raw: Uint8Array<ArrayBuffer>
  try {
    raw = fromB64(keyB64.trim())
  } catch {
    throw new SecretBoxError('SETTINGS_ENCRYPTION_KEY is not valid base64')
  }
  if (raw.length !== 32) throw new SecretBoxError('SETTINGS_ENCRYPTION_KEY must be 32 bytes (base64)')
  return crypto.subtle.importKey('raw', raw, 'AES-GCM', false, ['encrypt', 'decrypt'])
}

export async function encryptSecret(plain: string, keyB64: string | undefined): Promise<string> {
  const key = await importKey(keyB64)
  const iv = crypto.getRandomValues(new Uint8Array(12))
  const cipher = new Uint8Array(await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, enc.encode(plain)))
  return `${VERSION}.${toB64(iv)}.${toB64(cipher)}`
}

/** Plaintext, or null when the value isn't ours, was tampered with, or the key changed. */
export async function decryptSecret(sealed: string, keyB64: string | undefined): Promise<string | null> {
  const [version, iv, cipher] = sealed.split('.')
  if (version !== VERSION || !iv || !cipher) return null
  try {
    const key = await importKey(keyB64)
    const plain = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: fromB64(iv) }, key, fromB64(cipher))
    return dec.decode(plain)
  } catch {
    return null
  }
}
